import { TimerConfig, TimerSegment } from '../types/round-timer';

/**
 * Converts a number into a time-friendly format aka adds a 0 if needed
 * If the number is 20, it will return "20"
 * If the number is 1, it will return "01"
 */
export const formatNumber = (number: number): string => `0${number}`.slice(-2);

/**
 * Returns minutes and seconds from a total seconds value
 */
export const formattedTime = (time: number): { mins: string; secs: string } => {
  const mins = Math.floor(time / 60);
  const secs = time - mins * 60;
  return { mins: formatNumber(mins), secs: formatNumber(secs) };
};

/**
 * Returns minutes and seconds as a formatted time string (MM:SS)
 */
export const formattedTimeAsString = (time: number): string => {
  const { mins, secs } = formattedTime(time);
  return `${mins}:${secs}`;
};

/**
 * Calculates the total time of a workout based on the timerConfig
 */
export const getTotalTimerTime = (timerConfig: TimerConfig): number => {
  const { rounds, round, warmUp, coolDown } = timerConfig;
  const roundTime = rounds * (round.time + round.rest);
  const warmUpTime = warmUp.active ? warmUp.time : 0;
  const coolDownTime = coolDown.active ? coolDown.time : 0;
  return roundTime + warmUpTime + coolDownTime;
};

/**
 * Creates a timer sequence based on a timerConfig object
 * This makes it easier to track current timer progress
 */
export const getTimerSequenceBasedOnConfig = (timerConfig: TimerConfig): TimerSegment[] => {
  const { rounds, round, warmUp, coolDown } = timerConfig;
  const sequence: TimerSegment[] = [];

  // If it has a warm up, add it at the beginning
  if (warmUp.active) {
    sequence.push({
      type: 'extra',
      time: warmUp.time,
      label: warmUp.label,
      color: warmUp.color,
      textColor: warmUp.textColor,
    });
  }

  // Add all rounds based on config
  for (let index = 0; index < rounds; index++) {
    sequence.push({
      type: 'round',
      time: round.time,
      label: round.label,
      color: round.color,
      textColor: round.textColor,
    });
    sequence.push({
      type: 'rest',
      time: round.rest,
      label: round.restLabel,
      color: round.restColor,
      textColor: round.textColor,
    });
  }

  // If it has a cool down, add it to the end
  if (coolDown.active) {
    sequence.push({
      type: 'extra',
      time: coolDown.time,
      label: coolDown.label,
      color: coolDown.color,
      textColor: coolDown.textColor,
    });
  }

  return sequence;
};